import { useQuery } from '@tanstack/react-query';
import { useTenantContext } from './useTenantId';
import { tenantsService } from '../services/tenants.service';

export type ModuleKey =
  | 'compliance'
  | 'aiact'
  | 'it_security'
  | 'chat'
  | 'documents'
  | 'agents'
  | 'analytics'
  | 'executive_cockpit'
  | 'knowledge'
  | 'knowledge_management'
  | 'invoicing'
  | 'workflows'
  | 'api'
  | 'acor'
  | 'automation_agency'
  | 'finance'
  | 'finance_corpus'
  | 'admin';

/**
 * Tenants store the concrete `tenant_modules.module_key`, which often differs
 * from the semantic module name: on production `documents` is enabled for 49 of
 * 50 tenants while the literal `knowledge_management` exists on 3. A gate
 * matching only the semantic name would hide the surface from nearly everyone.
 *
 * Only list a key here when that divergence is real — a key with no entry keeps
 * plain exact matching. Must stay in sync with MODULE_SYNONYMS in the main
 * frontend (src/lib/tenant-nav-v2.ts) and the `accepted` tuple in the gateway's
 * ComplianceModuleMiddleware. If the three disagree, a visible nav entry leads
 * to a page whose API answers 404.
 */
const MODULE_SYNONYMS: Record<string, string[]> = {
  knowledge_management: ['knowledge_management', 'knowledge', 'documents', 'document', 'media'],
};

interface UseTenantModulesResult {
  enabledModules: string[];
  isModuleEnabled: (key: ModuleKey | string) => boolean;
  isLoading: boolean;
  isError: boolean;
  userRole: string;
  refetch: () => void;
}

export function useTenantModules(): UseTenantModulesResult {
  const { tenantId, isResolved, userRole } = useTenantContext();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tenant-modules', tenantId],
    queryFn: () => tenantsService.getModules(tenantId),
    enabled: isResolved && !!tenantId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const enabledModules = data?.enabled_modules ?? [];

  const isModuleEnabled = (key: ModuleKey | string): boolean => {
    if (!isResolved || isLoading) return true; // default to visible while loading
    // Array check rather than `?? [key]`: a plain object literal also resolves
    // inherited members, so a key like "constructor" would yield a function.
    const accepted = MODULE_SYNONYMS[key];
    return (Array.isArray(accepted) ? accepted : [key]).some((token) => enabledModules.includes(token));
  };

  return {
    enabledModules,
    isModuleEnabled,
    isLoading: isLoading || !isResolved,
    isError,
    userRole,
    refetch: () => { void refetch(); },
  };
}

export default useTenantModules;
